import * as React from 'react'
import { isEmpty } from 'lodash'
import { getOr } from 'lodash/fp'

import {
  Box,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  DialogActions,
  IconButton,
  Divider,
  Typography,
  Button,
} from '@design/pt-ui'
import Select from '../../components/Select'

import { useDirtySanchez } from '../../components/DirtySanchez'

import { Project, ProjectId } from '../../interfaces/entities/project'

import { IProjectService } from '../../interfaces/services/projects'

import DI from '../../app/di'

import { Formatset, FormatsetId } from '../../interfaces/entities/formatset'
import { translateWithUserLocale } from '../../utils/i18n'
import { toast } from 'react-toastify'
import { Platform } from '../../interfaces/entities/platform'
import { Client } from '../../interfaces/entities/client'

type SelectOption = {
  value: string
  label: string
}

interface IFormatsetImportProps {
  open: boolean
  activator?: any
  project: Project
  platforms: Platform[]
  onCloseDialog: () => void
  onOpen?: () => void
  onClose?: () => void
  confirmClose?: boolean
  getProject: (id: ProjectId) => void
  showImportFormatsetMessage: (message: string) => void
}

interface IState {
  dialogShown: boolean
  error: string | null
  isImporting: boolean
  formatsetIds: FormatsetId[]
  options: {
    allProjects: Project[] | null
    projects: Project[] | null
    platforms: Platform[]
    formatsets: Formatset[] | null
    formatsetsByProject: Formatset[] | null
    clients: Client[] | null
  }
  selectedValues: {
    project: SelectOption | null
    platform: SelectOption | null
    formatsets: SelectOption[] | null
    client: SelectOption | null
  }
}

function FormatsetImport(props: IFormatsetImportProps) {
  const defaultProject = {
    value: props.project.id,
    label: props.project.name,
  }
  const getInitialState: (props: IFormatsetImportProps) => IState = (
    props: IFormatsetImportProps,
  ) => {
    return {
      dialogShown: false,
      formatsetIds: [],
      isImporting: false,
      error: null,
      options: {
        allProjects: null,
        projects: null,
        formatsets: props.project.formatsets,
        formatsetsByProject: props.project.formatsets,
        platforms: props.platforms,
        clients: null,
      },
      selectedValues: {
        project: defaultProject,
        platform: null,
        formatsets: null,
        client: null,
      },
    }
  }

  const [state, setState] = React.useState<IState>(getInitialState(props))

  React.useEffect(() => {
    state.error !== null && toast.error(state.error)
  }, [state.error])

  const getProjectService = () => {
    return DI.get('ProjectService') as IProjectService
  }

  const initialize = async () => {
    const projectService = getProjectService()
    const projectsResponse = await projectService.getProjects({ perPage: 9999 })

    if (projectsResponse.type === 'error') {
      setState((state) => ({
        ...state,
        error: 'Could not load projects.',
      }))
    } else {
      const projects = projectsResponse.payload
      setState((state) => ({
        ...state,
        options: {
          ...state.options,
          allProjects: projects,
          projects: projects,
        },
      }))
    }

    const clientsResponse = await projectService.getClients()
    if (clientsResponse.type === 'error') {
      setState((state) => ({
        ...state,
        error: 'Could not load clients.',
      }))
    } else {
      const clients = clientsResponse.payload
      setState((state) => ({
        ...state,
        options: {
          ...state.options,
          clients: clients,
        },
      }))
    }
  }

  const handlePlatformChange = (value: any) => {
    setState((state) => ({
      ...state,
      selectedValues: {
        ...state.selectedValues,
        platform: value,
      },
      options: {
        ...state.options,
        formatsets: null,
      },
    }))

    if (state.options.platforms === null || state.options.projects === null) {
      return
    }

    const project =
      state.options.projects.find(
        (p) => p.id === state.selectedValues.project?.value,
      ) || null

    if (project === null || state.options.formatsetsByProject === null) {
      return
    }

    setState((state) => ({
      ...state,
      selectedValues: {
        ...state.selectedValues,
        platform: value,
      },
      options: {
        ...state.options,
        formatsets:
          state.options.formatsetsByProject &&
          state.options.formatsetsByProject.filter(
            (fs) => fs.type === value.value,
          ),
      },
    }))

    handleFormatsetsChange([])
  }

  const handleClientChange = (value: any) => {
    const newProjects = state?.options?.allProjects
      ? state.options.allProjects.filter((p) => p.client.id === value.value)
      : null

    setState((state) => ({
      ...state,
      selectedValues: {
        ...state.selectedValues,
        project: null,
        client: value,
      },
      options: {
        ...state.options,
        formatsets: [],
        formatsetsByProject: [],
        projects: newProjects,
      },
    }))
  }

  const handleProjectChange = async (value: any) => {
    setState((state) => ({
      ...state,
      selectedValues: {
        ...state.selectedValues,
        project: value,
        platform: null,
      },
      options: {
        ...state.options,
        formatsets: null,
      },
    }))

    if (state.options.projects === null) {
      return
    }

    const projectId = value.value as string

    const project =
      state.options.projects.find((p) => p.id === projectId) || null

    if (project == null) {
      return
    }

    const formatsetsResponse = await getProjectService().getFormatsets({
      projectId: projectId,
    })
    if (formatsetsResponse.type === 'error') {
      setState((state) => ({
        ...state,
        error: 'Could not load formatsets.',
      }))
    } else {
      const formatsets = formatsetsResponse.payload
      setState((state) => ({
        ...state,
        options: {
          ...state.options,
          formatsets: formatsets,
          formatsetsByProject: formatsets,
        },
      }))
    }
  }

  const handleFormatsetsChange = (value: any) => {
    if (state.options.formatsets === null) {
      return
    }

    const formatsetIds = value.map((value: any) => value.value) as string[]

    setState((state) => ({
      ...state,
      selectedValues: {
        ...state.selectedValues,
        formatsets: value,
      },
      formatsetIds: formatsetIds,
    }))
  }

  const handleImport = async () => {
    const projectId = props.project.id
    const ids = state.formatsetIds
    const selectedPlatform = getOr('', 'platform.value', state.selectedValues)

    try {
      setState((state) => ({
        ...state,
        isImporting: true,
      }))

      const imported = await getProjectService().importFormatsets(
        projectId,
        ids,
        selectedPlatform,
      )
      if (imported.type === 'error') {
        setState((state) => ({
          ...state,
          error: 'Error while importing formatset',
          isImporting: false,
        }))
      } else {
        let namesArray: string[] = []
        if (imported.payload.invalidFormatsets.length > 0) {
          state.options.formatsets?.forEach((fo) => {
            if (imported.payload.invalidFormatsets.includes(fo.id)) {
              namesArray.push(fo.name)
            }
          })
          props.showImportFormatsetMessage(namesArray.join(', '))
        }
        setState((state) => ({
          ...state,
          error: null,
          isImporting: false,
        }))
        if (imported.payload.result) {
          toast.success(
            translateWithUserLocale(
              'creative.import_formatset(s)_toast_success',
            ),
          )
        } else {
          toast.warn(
            translateWithUserLocale('creative.import_formatset(s)_toast_warn'),
          )
        }
      }
    } catch (e) {
      console.error(e)
      setState((state) => ({
        ...state,
        error: translateWithUserLocale('error.something_went_wrong_apologetic'),
        isImporting: false,
      }))
    }

    props.getProject(projectId)
    handleClose()
  }

  const { clean } = useDirtySanchez()

  const handleOpen = () => {
    setState((state) => ({
      ...state,
      dialogShown: true,
    }))

    initialize()
  }

  const handleClose = () => {
    const initial = getInitialState(props)

    if (props.confirmClose) {
      setState((state) => ({
        ...state,
        dialogShown: true,
      }))
    }
    if (
      (state.dialogShown && props.onClose) ||
      (props.onClose && !props.confirmClose)
    ) {
      props.onClose()
      clean('projects')
      setState((state) => ({
        ...initial,
        dialogShown: false,
      }))
    }
  }

  return (
    <>
      {props.activator != null ? (
        <Box component="span" onClick={handleOpen} sx={{ display: 'inherit' }}>
          {props.activator}
        </Box>
      ) : null}

      <Dialog
        sx={{ minHeight: '50vh' }}
        disableRestoreFocus={true}
        open={state.dialogShown}
        onClose={handleClose}
        maxWidth="lg">
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <DialogTitle sx={{ margin: '12px 0' }}>
            {translateWithUserLocale('project.import_formatset(s)_title')}
          </DialogTitle>
          <DialogActions>
            <IconButton onClick={handleClose} sx={{ color: 'primary.main' }}>
              <Icon>close</Icon>
            </IconButton>
          </DialogActions>
        </Box>
        <Divider />
        <DialogContent
          sx={{
            backgroundColor: 'custom.gray.background',
            paddingTop: '25px',
          }}>
          <Paper sx={{ marginBottom: '25px' }}>
            <div style={{ width: 600, padding: 35 }}>
              <Typography>
                {translateWithUserLocale('project.import_formatset(s)_client')}
              </Typography>
              <Select
                isLoading={state.options.clients ? false : true}
                onChange={handleClientChange}
                value={state.selectedValues.client}
                options={(state.options.clients || []).map((client) => {
                  return {
                    value: client.id,
                    label: client.name,
                  }
                })}
                isClearable={false}
                id="cp-select-client-import-fs"
              />
            </div>

            <div style={{ width: 600, padding: 35 }}>
              <Typography>
                {translateWithUserLocale('project.import_formatset(s)_project')}
              </Typography>
              <Select
                isLoading={state.options.projects ? false : true}
                defaultValue={defaultProject}
                value={state.selectedValues.project}
                onChange={handleProjectChange}
                options={(state.options.projects || []).map((project) => {
                  return {
                    value: project.id,
                    label: project.name,
                  }
                })}
                isClearable={false}
                id="cp-select-project-import-fs"
              />
            </div>
            <div style={{ width: 600, padding: 35 }}>
              <Typography>
                {translateWithUserLocale(
                  'project.import_formatset(s)_platform',
                )}
              </Typography>
              <Select
                isLoading={state.options.platforms ? false : true}
                onChange={handlePlatformChange}
                value={state.selectedValues.platform}
                options={(state.options.platforms || []).map((platform) => {
                  return {
                    value: platform.value,
                    label: platform.label,
                  }
                })}
                isClearable={false}
                id="cp-select-platform-import-fs"
              />
            </div>
            <div style={{ width: 600, padding: 35 }}>
              <Typography>
                {translateWithUserLocale('project.import_formatset(s)')}
              </Typography>
              <Select
                isMulti={true}
                isDisabled={false}
                isLoading={state.options.formatsets ? false : true}
                onChange={handleFormatsetsChange}
                options={(state.options.formatsets || [])
                  .filter(function (formatset) {
                    if (formatset.invalid) {
                      return false
                    }
                    return true
                  })
                  .map((formatset) => {
                    return {
                      value: formatset.id,
                      label: formatset.name,
                    }
                  })}
                value={state.selectedValues.formatsets}
                isClearable={true}
                id="cp-select-formatset-import-fs"
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingBottom: 25,
                marginRight: 35,
              }}>
              <Button
                color="primary"
                variant="contained"
                disabled={
                  state.isImporting ||
                  isEmpty(state.formatsetIds) ||
                  !state.selectedValues.platform
                }
                onClick={handleImport}>
                {translateWithUserLocale(
                  'project.import_formatset(s)_button_submit',
                )}
              </Button>
            </div>
          </Paper>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default FormatsetImport
